import { FC, useState } from 'react'
import * as React from 'react'
import classNames from 'classnames'
import { CSSTransition } from 'react-transition-group'
import { matchPath, useLocation } from 'react-router-dom'

import useIsActivePath from '../../../helpers/useIsActivePath'
import { FontFamilies, color } from '../../../style/variables'
import { CustomLink } from '../../links'
import { Link } from '../../../helpers/navStructure'
import Tier3MenuItem from './Tier3MenuItem'
import Chevron from './Chevron'
import useIsExactActivePath from './useIsExactActivePath'
import { replaceLocale } from '../../../helpers/urls'

interface Tier2NavItemProps {
  href: string
  label: string
  childLinks: Link[]
  exactMatch?: boolean
}

const transitionDuration = 250

/* eslint-disable react-hooks/rules-of-hooks */
const Tier2NavItem: FC<Tier2NavItemProps> = ({ href, label, childLinks, exactMatch }) => {
  const url = replaceLocale(href)
  const isActive = exactMatch ? useIsExactActivePath(url) : useIsActivePath(url, [])
  const [menuVisible, setMenuVisible] = useState(false)
  const classes = classNames('tier2-nav-item', {
    active: isActive,
    'menu-open': menuVisible,
  })
  const tier3MenuClasses = classNames('tier3-menu', {
    'menu-open': menuVisible,
  })
  const hasMenu = childLinks.length > 0
  const location = useLocation()

  const openMenu = (): void => {
    if (hasMenu) {
      setMenuVisible(true)
    }
  }

  const closeMenu = (): void => {
    if (hasMenu) {
      setMenuVisible(false)
    }
  }

  return (
    <>
      <style jsx>{`
        .tier2-nav-item {
          text-transform: uppercase;
          text-decoration: none;
          height: 64px;
          font-family: ${FontFamilies.barlow};
          font-size: 15px;
          font-weight: 500;
          font-stretch: normal;
          font-style: normal;
          line-height: 4.27;
          letter-spacing: 2px;
          text-align: center;
          color: ${color.textStandard};
          display: inline-block;
          width: 100%;
          white-space: nowrap;
          border-bottom: 4px solid white;
          transition: all ${transitionDuration}ms;
        }
        .tier2-nav-item.menu-open {
          border-bottom: 4px solid ${color.brandPrimary};
        }
        .tier2-nav-item:hover {
          color: ${color.brandPrimary};
        }
        .active {
          font-weight: bold;
          color: ${color.brandPrimary};
          border-bottom: 2px solid ${color.brandPrimary};
        }
        .tier3-menu {
          position: absolute;
          display: none;
          flex-direction: column;
          width: 100%;
          z-index: 1;
          transition: all ${transitionDuration}ms;
          opacity: 1;
        }
        .tier2-nav-item-wrapper {
          position: relative;
          flex: 1 1 0px;
        }
        .chevron {
          position: absolute;
          right: 23px;
          top: 0px;
        }

        .tier3-menu-enter {
          opacity: 0;
          display: flex;
        }
        .tier3-menu-enter-done {
          display: flex;
        }

        .tier3-menu-enter-active {
          opacity: 1;
          transition: all ${transitionDuration}ms;
          transition-timing-function: ease-in-out;
        }
        .tier3-menu-exit-active {
          opacity: 0;
          transition: all ${transitionDuration}ms;
          transition-timing-function: ease-in-out;
        }

        .tier3-menu-exit {
          display: flex;
          opacity: 0;
        }
        .tier3-menu-exit-done {
          display: none;
        }
      `}</style>
      <li className="tier2-nav-item-wrapper" onMouseLeave={closeMenu}>
        {location.pathname.startsWith('/virgin-red') || matchPath(location.pathname, '/:locale/virgin-red') ? (
          <CustomLink to={url}>
            <div className={classes} onMouseOver={openMenu} onClick={openMenu}>
              {label}
              {hasMenu && (
                <div className="chevron">
                  <Chevron fill={menuVisible || isActive ? color.brandPrimary : '#000000'} />
                </div>
              )}
            </div>
          </CustomLink>
        ) : (
          <a href={url} className={classes} onMouseOver={openMenu} onClick={openMenu}>
            {label}
            {hasMenu && (
              <div className="chevron">
                <Chevron fill={menuVisible || isActive ? color.brandPrimary : '#000000'} />
              </div>
            )}
          </a>
        )}
        <CSSTransition in={menuVisible} timeout={transitionDuration} classNames="tier3-menu">
          <div className={tier3MenuClasses}>
            {childLinks.map((cl) => (
              <Tier3MenuItem key={cl.href} href={cl.href} label={cl.label} onNavigate={closeMenu} />
            ))}
          </div>
        </CSSTransition>
      </li>
    </>
  )
}

export default Tier2NavItem
